// UC Opportunity Companion V2 assessment configuration.
// The questions intentionally separate career history, transferable capability,
// non-IT exposure, readiness, family model, and life-support consent.

const option = (label, score = {}, extra = {}) => ({ label, score, ...extra });

// These labels are the visitor-facing opportunity areas. Keep this mapping in
// one place so the question flow, sector summary, and specific-recommendation
// engine all use the same meaning.
export const V2_SECTOR_EXPLORATION = {
  "Food and hospitality": { flowKey: "small_industries", scoreKey: "small_industries", catalogSector: "Small Industries" },
  "Education and family services": { flowKey: "education", scoreKey: "education", catalogSector: "Education" },
  "Property and home services": { flowKey: "real_estate", scoreKey: "real_estate", catalogSector: "Real Estate" },
  "Senior and community support": { flowKey: "healthcare", scoreKey: "healthcare", catalogSector: "Healthcare" },
  "Financial and administrative services": { flowKey: "finance", scoreKey: "finance", catalogSector: "Finance" },
  "Insurance support services": { flowKey: "insurance", scoreKey: "insurance", catalogSector: "Insurance" },
  "Banking and financial administration": { flowKey: "banking", scoreKey: "banking", catalogSector: "Banking" },
  "Legal and compliance administration": { flowKey: "legal", scoreKey: "legal", catalogSector: "Legal" },
  "Research and data services": { flowKey: "research", scoreKey: "research", catalogSector: "Research" },
  "Retail, trade, and distribution": { flowKey: "small_industries", scoreKey: "small_industries", catalogSector: "Small Industries" },
  "Transportation and logistics": { flowKey: "transportation", scoreKey: "travel", catalogSector: "Transportation" },
  "Events and community services": { flowKey: "travel", scoreKey: "travel", catalogSector: "Travel / Events" },
  "Wellness and personal services": { flowKey: "healthcare", scoreKey: "healthcare", catalogSector: "Healthcare" },
  "Small manufacturing and skilled services": { flowKey: "small_industries", scoreKey: "small_industries", catalogSector: "Small Industries" },
  "Franchise or managed business": { flowKey: "small_industries", scoreKey: "small_industries", catalogSector: "Small Industries" },
  "Technology-enabled non-IT services": { flowKey: "it_research", scoreKey: "it", catalogSector: "IT / Research" },
  "Professional-field opportunities": { flowKey: "it_research", scoreKey: "it", catalogSector: "IT / Research" },
};

// These answers mean the visitor has not selected any of the substantive
// choices. Keeping them exclusive avoids contradictory assessment evidence.
export const isExclusiveMultiSelectOption = (label = "") =>
  /^(none of (these|the above)|nothing currently|not sure|i am not sure|not yet decided|i need guidance)$/i.test(
    String(label).trim()
  );

export const updateMultiSelectAnswer = (currentValues = [], label, selectionLimit = 99) => {
  const current = Array.isArray(currentValues) ? currentValues : [];

  if (isExclusiveMultiSelectOption(label)) {
    return current.includes(label) ? [] : [label];
  }

  if (current.includes(label)) {
    return current.filter((item) => item !== label);
  }

  const substantiveValues = current.filter((item) => !isExclusiveMultiSelectOption(item));
  return substantiveValues.length < selectionLimit ? [...substantiveValues, label] : current;
};

export const V2_QUESTIONS = [
  {
    id: "v2_intent_1",
    eyebrow: "Your UC goal",
    text: "What are you mainly hoping UC can help you accomplish?",
    answerType: "multi_select_max_3",
    selectionLimit: 3,
    options: [
      option("Build an additional income stream outside my current profession", { action_ready: 1, non_it_alignment: 2 }),
      option("Start a small business", { action_ready: 2, business_growth: 1 }),
      option("Prepare for long-term business ownership", { business_growth: 2, scalability: 2 }),
      option("Find flexible work", { low_risk_path: 1, lifestyle: 1 }),
      option("Build income for my spouse or family member", { family_model: 2, family_priority: 1 }),
      option("Learn a practical new skill", { training_support: 2, learning_first: 1 }),
      option("Reduce dependence on one employer", { action_ready: 1, low_risk_path: 1 }),
      option("Prepare for a career interruption", { low_risk_path: 2, mentor_support: 1 }),
      option("Explore a return-to-India income plan", { cross_border: 2, mentor_support: 1 }),
      option("Find a local community-based opportunity", { community_support: 2, non_it_alignment: 1 }),
      option("Grow an existing business", { business_growth: 3 }),
      option("Find employment", { employment_support: 2 }),
      option("Contribute as a mentor, provider, sponsor, or volunteer", { ecosystem: 3 }),
      option("I am not yet sure", { mentor_support: 2 }),
    ],
  },
  {
    id: "v2_non_it_preference",
    eyebrow: "Path preference",
    text: "Should the Companion actively explore opportunities outside your current professional field?",
    options: [
      option("Yes, prioritize non-IT opportunities", { non_it_alignment: 5, execution_advantage: 1 }),
      option("Yes, but use my professional experience as an advantage", { tech_enabled_non_it: 4, non_it_alignment: 3 }),
      option("Show me both non-IT and professional-field opportunities", { balanced_path: 3 }),
      option("I only want opportunities related to my current profession", { pure_it_allowed: 5 }),
      option("I am not sure", { mentor_support: 2, discovery_needed: 2 }),
    ],
  },
  {
    id: "v2_12m_goal",
    eyebrow: "Next 12 months",
    text: "Which result would be most valuable during the next 12 months?",
    options: [
      option("Earn my first additional $500 per month", { low_risk_path: 2 }),
      option("Build a reliable part-time income", { action_ready: 2 }),
      option("Replace part of my salary", { action_ready: 2, risk_awareness: 1 }),
      option("Create income for my spouse or household", { family_model: 2 }),
      option("Test a business before investing heavily", { low_risk_path: 3 }),
      option("Obtain a license or certification", { learning_first: 2, licensing: 2 }),
      option("Purchase or operate a small business", { business_growth: 3, investment_capacity: 1 }),
      option("Build a business that can employ others", { scalability: 3, business_growth: 2 }),
      option("Prepare for a future move or return to India", { cross_border: 2 }),
      option("I need guidance before deciding", { mentor_support: 3 }),
    ],
  },
  {
    id: "career_situation",
    eyebrow: "Current foundation",
    text: "Which statement best describes your current professional situation?",
    options: [
      option("Employed full-time", { stability: 2 }), option("Employed part-time", { availability: 1 }),
      option("Self-employed", { business_growth: 1 }), option("Business owner", { business_growth: 2 }),
      option("Between jobs", { low_risk_path: 2 }), option("Student", { learning_first: 2 }),
      option("Homemaker or caregiver", { family_model: 2 }), option("Retired", { flexibility: 2 }),
      option("Newly arrived in the United States", { settlement_support: 2 }), option("Other"),
    ],
  },
  {
    id: "career_field",
    eyebrow: "Professional background",
    text: "Which broad field best describes your primary work experience?",
    options: [
      option("Information technology", { it_background: 4 }), option("Healthcare"), option("Education"),
      option("Finance or accounting"), option("Insurance"), option("Retail"), option("Hospitality"),
      option("Construction or skilled trades"), option("Manufacturing"), option("Transportation or logistics"),
      option("Government"), option("Legal services"), option("Real estate"), option("Sales and marketing"),
      option("Human resources"), option("Community or nonprofit work"), option("Home and family management"), option("Other"),
    ],
  },
  {
    id: "household_dependence",
    eyebrow: "Transition planning",
    text: "How dependent is your household currently on income from your main job?",
    options: [option("Fully dependent", { risk_awareness: 2 }), option("Mostly dependent", { risk_awareness: 1 }),
      option("Partly dependent"), option("Minimally dependent", { action_ready: 1 }), option("Not currently earning", { low_risk_path: 2 }), option("Prefer not to answer")],
  },
  {
    id: "responsibilities",
    eyebrow: "Transferable capability",
    text: "Which types of responsibilities have you personally handled?",
    answerType: "multi_select_max_5",
    selectionLimit: 5,
    options: [
      option("Solving unclear or difficult problems", { transferable: 2 }), option("Managing people", { leadership: 2 }),
      option("Coordinating vendors", { vendor: 2 }), option("Planning schedules and deadlines", { coordination: 2 }),
      option("Working directly with customers", { customer: 2 }), option("Selling or persuading", { sales: 2 }),
      option("Teaching or explaining", { teaching: 2 }), option("Maintaining accurate records", { documentation: 2 }),
      option("Managing money or budgets", { budgeting: 2 }), option("Handling confidential information", { privacy: 1 }),
      option("Inspecting quality", { quality: 2 }), option("Negotiating", { negotiation: 2 }), option("Organizing events", { events: 2 }),
      option("Caring for children, seniors, or family members", { care: 2 }), option("Cooking or food preparation", { food: 2 }),
      option("Driving or transportation coordination", { transport: 2 }), option("Repairing or maintaining things", { hands_on: 2 }),
      option("Managing a home or rental property", { property: 2 }), option("Community organizing", { community: 2 }),
      option("Recruiting or evaluating people", { leadership: 1 }), option("Creating written or visual content", { content: 1 }),
      option("Following regulated procedures", { compliance: 2 }), option("None of these"), option("Other"),
    ],
  },
  {
    id: "transferable_depth",
    eyebrow: "Evidence strength",
    text: "How independently have you performed your selected responsibilities?",
    options: [option("Observed someone else", { evidence: 1 }), option("Assisted someone", { evidence: 2 }),
      option("Performed under supervision", { evidence: 3 }), option("Performed independently", { evidence: 4 }),
      option("Managed others performing it", { evidence: 5, leadership: 1 }), option("Taught others", { evidence: 5, teaching: 1 })],
  },
  {
    id: "practical_exposure",
    eyebrow: "Non-IT exposure",
    text: "Outside your formal profession, which activities have you already performed or helped with?",
    answerType: "multi_select_max_4",
    selectionLimit: 4,
    options: [
      option("Cooking or catering", { food: 3 }), option("Tutoring or childcare", { teaching: 3 }), option("Senior assistance", { care: 3 }),
      option("Event planning", { events: 3 }), option("Religious or community-event coordination", { events: 3, community: 1 }),
      option("Property or tenant management", { property: 3 }), option("Home repair or maintenance", { hands_on: 3 }),
      option("Cleaning or organizing", { operations: 2 }), option("Transportation assistance", { transport: 3 }),
      option("Buying and reselling goods", { retail: 3, sales: 1 }), option("Tax or bookkeeping assistance", { budgeting: 3 }),
      option("Insurance education or sales", { insurance: 3 }), option("Real-estate support", { property: 3 }), option("Travel planning", { travel: 3 }),
      option("Photography or media", { content: 2 }), option("Fitness or wellness support", { wellness: 3 }), option("Farming, gardening, or food production", { food: 2 }),
      option("Import or export activity", { logistics: 2 }), option("Retail-store support", { retail: 2 }), option("Restaurant or hospitality support", { hospitality: 3 }),
      option("Nonprofit volunteering", { community: 2 }), option("None yet"), option("Other"),
    ],
  },
  {
    id: "v2_sector_exploration",
    eyebrow: "Opportunity exploration",
    text: "Which opportunity areas would you be willing to explore first?",
    answerType: "multi_select_max_3",
    selectionLimit: 3,
    options: [
      option("Food and hospitality", { food: 3, small_industries: 3 }), option("Education and family services", { teaching: 3, education: 3 }),
      option("Property and home services", { property: 3, real_estate: 3 }), option("Senior and community support", { care: 3, healthcare: 3 }),
      option("Financial and administrative services", { finance_support: 3, finance: 3 }), option("Insurance support services", { insurance: 3 }),
      option("Banking and financial administration", { banking: 3 }), option("Legal and compliance administration", { legal: 3 }),
      option("Research and data services", { research: 3 }), option("Retail, trade, and distribution", { retail: 3, small_industries: 3 }),
      option("Transportation and logistics", { transport: 3, travel: 3 }), option("Events and community services", { events: 3, travel: 3 }),
      option("Wellness and personal services", { wellness: 3, healthcare: 3 }), option("Small manufacturing and skilled services", { hands_on: 3, small_industries: 3 }),
      option("Franchise or managed business", { scalability: 3, small_industries: 3 }), option("Technology-enabled non-IT services", { tech_enabled_non_it: 3, it: 3 }),
      option("Professional-field opportunities", { pure_it_allowed: 2, it: 2 }), option("I need a General Advisor to help me choose", { mentor_support: 3 }),
    ],
  },
  {
    id: "activities_consider",
    eyebrow: "Practical openness",
    text: "Which activities would you still consider even if they are different from your current professional status?",
    answerType: "multi_select_max_4",
    selectionLimit: 4,
    options: [option("Customer-facing local service", { customer: 2 }), option("Physical or hands-on work", { hands_on: 2 }),
      option("Home-based service", { home_based: 2 }), option("Weekend service", { weekend: 2 }), option("Family-operated business", { family_model: 3 }),
      option("Licensed professional service", { licensing: 2 }), option("Retail or resale", { retail: 2 }), option("Food-related business", { food: 2 }),
      option("Property-related service", { property: 2 }), option("Transportation or logistics", { transport: 2 }), option("Education or childcare", { teaching: 2 }),
      option("Senior and family support", { care: 2 }), option("Event and community service", { events: 2 }), option("Franchise or established business model", { scalability: 2 }),
      option("Investment-supported business", { investment_capacity: 2 }), option("None of these"), option("I need guidance", { mentor_support: 2 })],
  },
  {
    id: "identity_constraints",
    eyebrow: "Work-model fit",
    text: "When considering an additional-income activity, which concerns would matter to you?",
    answerType: "multi_select_max_3",
    selectionLimit: 3,
    options: [option("It should match my professional status", { status_constraint: 1 }), option("My family may not support it", { family_constraint: 1 }),
      option("My community may judge the work", { status_constraint: 1 }), option("I would prefer not to do physical work", { hands_on_block: 2 }),
      option("I am uncomfortable selling", { sales_block: 2 }), option("I do not want strangers entering my home", { home_block: 2 }),
      option("I do not want to travel locally", { travel_block: 2 }), option("I do not want to manage employees", { leadership_block: 1 }),
      option("I do not want licensing requirements", { licensing_block: 2 }), option("I prefer work that can later become a larger business", { scalability: 2 }),
      option("I am open to starting small if the pathway is respectable and scalable", { openness: 3 }), option("None of these")],
  },
  {
    id: "family_participant",
    eyebrow: "Family operating model",
    text: "Who may actively participate in the opportunity?",
    options: [option("Only me"), option("Me and my spouse or partner", { family_model: 3 }), option("Primarily my spouse or partner", { family_model: 3 }),
      option("Another adult family member", { family_model: 2 }), option("Multiple family members", { family_model: 3 }), option("I prefer to invest while someone else operates", { investment_capacity: 2 }), option("Not yet decided", { mentor_support: 1 })],
  },
  {
    id: "reality_hours",
    eyebrow: "Real-life availability",
    text: "How many dependable hours can you commit each week for at least six months?",
    options: [option("Less than 3 hours", { hours: 1 }), option("3–5 hours", { hours: 2 }), option("6–10 hours", { hours: 3 }), option("11–20 hours", { hours: 4 }), option("More than 20 hours", { hours: 5 }), option("My availability changes significantly", { hours: 1, availability_block: 1 })],
  },
  {
    id: "reality_setting",
    eyebrow: "Real-life availability",
    text: "Which work settings are acceptable?",
    answerType: "multi_select_max_3",
    selectionLimit: 3,
    options: [option("From home", { home_based: 2 }), option("At a customer location", { customer: 2 }), option("In a shared commercial location", { commercial: 2 }), option("Outdoors", { outdoors: 2 }), option("Driving locally", { transport: 2 }), option("Remote or online", { remote: 2 }), option("Managing others rather than performing the work", { leadership: 2 }), option("Any safe and lawful environment", { flexibility: 2 })],
  },
  {
    id: "vision",
    eyebrow: "Long-term vision",
    text: "If a supporting-income activity becomes stable, what would you eventually like to build?",
    options: [option("A professional consulting company", { pure_it_allowed: 1 }), option("A local service company", { non_it_alignment: 3, scalability: 2 }), option("A family-operated business", { family_model: 3, scalability: 2 }), option("A retail or distribution business", { retail: 2, scalability: 2 }), option("A food or hospitality business", { food: 2, scalability: 2 }), option("A property-related business", { property: 2, scalability: 2 }), option("An education business", { teaching: 2, scalability: 2 }), option("A healthcare or wellness-related business", { wellness: 2, scalability: 2 }), option("A financial or insurance-services business", { finance_support: 2, licensing: 2 }), option("A franchise", { scalability: 3, investment_capacity: 2 }), option("A manufacturing or product business", { hands_on: 2, scalability: 2 }), option("A social enterprise or nonprofit initiative", { community: 3 }), option("An India–U.S. cross-border business", { cross_border: 3 }), option("An investment portfolio", { investment_capacity: 3 }), option("I do not yet know", { mentor_support: 2 })],
  },
  {
    id: "ecosystem_contribution",
    eyebrow: "Your UC contribution",
    text: "Besides receiving support, is there anything you may be able to contribute to another UC member?",
    answerType: "multi_select_max_4",
    selectionLimit: 4,
    options: [option("Mentoring", { ecosystem: 2 }), option("Technical guidance", { ecosystem: 2, it_background: 1 }), option("Business guidance", { ecosystem: 2 }), option("Interview support", { ecosystem: 2 }), option("Community connections", { ecosystem: 2 }), option("Job or internship opportunities", { ecosystem: 3 }), option("Vendor or contract opportunities", { ecosystem: 3 }), option("Training", { ecosystem: 2 }), option("Event speaking", { ecosystem: 2 }), option("Volunteering", { ecosystem: 2 }), option("Sponsorship", { ecosystem: 3 }), option("Facility or equipment access", { ecosystem: 2 }), option("Transportation help", { ecosystem: 2 }), option("Cultural or language support", { ecosystem: 2 }), option("Nothing currently"), option("Not sure", { mentor_support: 1 })],
  },
  {
    id: "life_support_consent",
    eyebrow: "Optional family support",
    text: "Would you like UC to create a separate optional family and life-journey support profile?",
    options: [
      option("Yes, show me family and life-support resources", { family_priority: 1 }),
      option("Maybe later", { family_priority: 1 }),
      option("No, keep this assessment focused on opportunity planning"),
    ],
  },
];

export const V2_OPPORTUNITY_CLASSIFICATION = {
  NON_IT: "NON_IT",
  TECHNOLOGY_ENABLED_NON_IT: "TECHNOLOGY_ENABLED_NON_IT",
  IT_ADJACENT: "IT_ADJACENT",
  PURE_IT: "PURE_IT",
};
